<script>
  import { onDestroy, onMount } from 'svelte';
  import { formatTimeLabel } from '$lib/utils/datetime';
  import BubbleActions from '@components/messenger/messages/BubbleActions.svelte';
  import ContextMenu from '@components/messenger/messages/ContextMenu.svelte';
  import { isMobile } from '$lib/device.js';
  import {
    sendMessage,
    pinMessageWS,
    unpinMessageWS,
    replyingTo,
    loadMessagesWS,
    messages,
    conversationId,
    selectionMode,
    selectedMessageIds
  } from '$lib/websocket-client';
  import { tick } from 'svelte';
  import { get } from 'svelte/store';
  import PdfPreview from '@components/messenger/PdfViewer.svelte';
  import MessagePreview from '@components/messenger/MessagePreview.svelte';
  import ShareMessageBubble from '@components/messenger/ShareMessageBubble.svelte';
  // import { normalizeUrl } from '$lib/utils';
  import { normalizeUrl, preferInlineMediaUrl, getMediaUrlHints, isPDF, isImage, isVideo } from '$lib/utils';

  import Sticker from '@components/messenger/Sticker.svelte';
  import CallMessageBubble from '@components/messenger/messages/CallMessageBubble.svelte';
  import LinkMessageBubble from '@components/messenger/messages/LinkMessageBubble.svelte';
  import LocationMessageBubble from '@components/messenger/messages/LocationMessageBubble.svelte';
  import MessageSelectionOverlay from '@components/messenger/messages/MessageSelectionOverlay.svelte';
  import RecalledMessageBubble from '@components/messenger/messages/RecalledMessageBubble.svelte';
  import TextMessageBubble from '@components/messenger/messages/TextMessageBubble.svelte';
  import MediaVideoPlayer from '@components/messenger/MediaVideoPlayer.svelte';
  import MediaAlbumPreview from '@components/messenger/previews/MediaAlbumPreview.svelte';
  import MediaFilePreview from '@components/messenger/previews/MediaFilePreview.svelte';
  import MediaImagePreview from '@components/messenger/previews/MediaImagePreview.svelte';
  import PreviewMedia from '@components/messenger/previews/PreviewMedia.svelte';
  import VoiceMessage from '@components/messenger/messages/VoiceMessage.svelte';

  export let onShareMessage = null;
  export let msg;
  export let mediaMessages = [];
  export let myId;
  export let previousMsg = null;
  export let interactionMode = 'desktop';
  export let isPinned = false;
  let msgEl;
  let contextMenuEl;
  //const isTouch = matchMedia('(pointer: coarse)').matches;

  let replyEl;
  let optimisticReaction = null;
  let reactionBursts = [];
  let reactionBurstSeq = 0;
  const reactionBurstTimers = new Map();
  const REACTION_BURST_DURATION = 1180;
  const REACTION_BURST_COLORS = ['#ffffff', '#ffd166', '#ff8f70', '#66c2ff', '#7ee081', '#d18bff'];
  const REACTION_BURST_ICONS = ['✨', '💫', '🌟', '💥', '✦'];
  const REACTION_BURST_SIZE_SCALE = 10;
  let previousMessageId = msg?.id;
  let messageShellHovered = false;
  let mouseSelectStart = null;
  let suppressClickAfterSelectMany = false;
  const SELECT_DRAG_DISTANCE = 8;

  //export let showStatus = false;

  let longPressTimer = null;
  const LONG_PRESS_DELAY = 450; // ms
  const CONTEXT_PADDING = 12;

  // ===== CONTEXT MENU STATE =====
  let contextMsg = null;
  let menuPos = { x: 0, y: 0 };
  let contextMenuMode = 'full';
  let contextListenersBound = false;
  let contextMenuReady = false;

  // ===== GALLERY =====
  let showGallery = false;
  let previewAlbumIndex = 0;

  function openMediaPreview(albumIndex = 0) {
    previewAlbumIndex = albumIndex;
    showGallery = true;
  }

  //$: console.log('msg: ', msg);
  //$: console.log('msg.from === myId: ', msg.message, msg.from, myId);

  const fromMe = String(msg.from) === String(myId);
  const sameSender = previousMsg && previousMsg.from === msg.from;
  $: useMobileActions = $isMobile || interactionMode === 'mobile';

  /* ================= HELPERS ================= */
  $: isSelected = $selectedMessageIds.has(msg.id);

  // const isPDF = (url) => /\.pdf$/i.test(url);
  // const isVideo = (url) => /\.(mp4|webm|mov|ogg)$/i.test(url);
  // const isImage = (url) => !isVideo(url) && /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
  // const isFile = (url) => !isImage(url) && !isVideo(url) && !isPDF(url);

  function enterSelectionMode(msgId) {
    selectionMode.set(true);
    selectedMessageIds.update((s) => {
      const next = new Set(s);
      next.add(msgId);
      return next;
    });
  }

  function selectManyMessage(message = msg) {
    if (!message?.id) return;
    enterSelectionMode(message.id);
    closeContextMenu();
  }

  function isInteractiveMouseTarget(target) {
    return target instanceof Element && !!target.closest('button, a, input, textarea, select, [role="button"], .context-menu');
  }

  function clearMouseSelectDrag() {
    mouseSelectStart = null;
  }

  function handleMouseDown(event) {
    if (useMobileActions || $selectionMode || event.button !== 0 || isInteractiveMouseTarget(event.target)) return;
    mouseSelectStart = { x: event.clientX, y: event.clientY };
  }

  function handleMouseMove(event) {
    if (!mouseSelectStart || useMobileActions || $selectionMode) return;

    const distance = Math.hypot(event.clientX - mouseSelectStart.x, event.clientY - mouseSelectStart.y);
    if (distance < SELECT_DRAG_DISTANCE) return;

    event.preventDefault();
    document.getSelection?.()?.removeAllRanges?.();
    suppressClickAfterSelectMany = true;
    selectManyMessage(msg);
    clearMouseSelectDrag();
  }

  function handleMouseUp() {
    clearMouseSelectDrag();
  }

  function toggleSelect(msgId) {
    selectedMessageIds.update((s) => {
      const next = new Set(s);
      next.has(msgId) ? next.delete(msgId) : next.add(msgId);
      return next;
    });
  }

  function clearSelection() {
    selectedMessageIds.set(new Set());
    selectionMode.set(false);
    closeContextMenu();
  }

  function selectOnlyForShare(m) {
    if (!m?.id) return false;
    selectedMessageIds.set(new Set([m.id]));
    selectionMode.set(false);
    return true;
  }

  function shareMessage(m) {
    if (!selectOnlyForShare(m)) return;
    onShareMessage?.(m);
    closeContextMenu();
  }

  function quickShare(m) {
    if (!m) return;
    if (!selectOnlyForShare(m)) return;
    onShareMessage?.(m);
  }

  function clearReactionBurstTimer(burstId) {
    const timer = reactionBurstTimers.get(burstId);
    if (!timer) return;

    clearTimeout(timer);
    reactionBurstTimers.delete(burstId);
  }

  function removeReactionBurst(burstId) {
    reactionBursts = reactionBursts.filter((burst) => burst.id !== burstId);
    clearReactionBurstTimer(burstId);
  }

  function clearReactionBursts() {
    for (const burst of reactionBursts) {
      clearReactionBurstTimer(burst.id);
    }

    reactionBursts = [];
  }

  function spawnReactionBurst(reaction) {
    const burstId = ++reactionBurstSeq;
    const particles = Array.from({ length: 16 }, (_, index) => {
      const angle = -Math.PI / 2 + Math.PI * 1.35 * (index / 15) + (Math.random() * 0.14 - 0.07);
      const distance = 38 + Math.random() * 30;
      const lift = 18 + Math.random() * 22;
      const drift = Math.random() * 10 - 5;
      const isIcon = index % 4 === 0;

      return {
        id: `${burstId}-${index}`,
        x: 50,
        y: 50,
        tx: Math.cos(angle) * distance + drift,
        ty: Math.sin(angle) * distance - lift,
        delay: Math.round(Math.random() * 160),
        size: (isIcon ? 16 + Math.random() * 6 : 5 + Math.random() * 4) * REACTION_BURST_SIZE_SCALE,
        color: REACTION_BURST_COLORS[index % REACTION_BURST_COLORS.length],
        icon: isIcon ? (index % 2 === 0 ? reaction : REACTION_BURST_ICONS[index % REACTION_BURST_ICONS.length]) : null
      };
    });

    reactionBursts = [...reactionBursts, { id: burstId, reaction, particles }];

    const timer = setTimeout(() => {
      removeReactionBurst(burstId);
    }, REACTION_BURST_DURATION);

    reactionBurstTimers.set(burstId, timer);
  }

  function openDetailsMenu() {
    void openContextMenuNearBubble('details');
  }

  // function normalizeUrl(url) {
  //   const API_BASE = import.meta.env.VITE_API_BASE_URL;
  //   if (!url) return url;

  //   if (/^https?:\/\//i.test(url)) {
  //     const u = new URL(url);
  //     return `${API_BASE}${u.pathname}`;
  //   }

  //   return `${API_BASE}/${url.replace(/^\/+/, '')}`;
  // }

  function handleReplyTouchStart(e) {
    e.stopPropagation();
    scrollToOriginalMessage(msg.reply_to.id);
  }

  onMount(() => {
    if (!msgEl) return;

    msgEl?.addEventListener('touchstart', handleTouchStart, { passive: true });
    msgEl?.addEventListener('touchmove', handleTouchMove, { passive: true });
    msgEl?.addEventListener('touchend', handleTouchEnd, { passive: true });

    if (replyEl) {
      replyEl?.addEventListener('touchstart', handleReplyTouchStart, {
        passive: true
      });
    }

    return () => {
      msgEl?.removeEventListener('touchstart', handleTouchStart);
      msgEl?.removeEventListener('touchmove', handleTouchMove);
      msgEl?.removeEventListener('touchend', handleTouchEnd);

      replyEl?.removeEventListener('touchstart', handleReplyTouchStart);

      unbindContextListeners();
    };
  });

  onDestroy(() => {
    clearReactionBursts();
  });

  async function scrollToOriginalMessage(replyToId) {
    if (!replyToId) return;

    // 1️⃣ thử tìm ngay
    let el = document.getElementById(`msg-${replyToId}`);
    if (el) {
      jumpAndHighlight(el);
      return;
    }

    // 2️⃣ nếu chưa có → load dần tin cũ
    let safety = 0;

    while (!el && safety < 20) {
      safety++;

      const currentMessages = get(messages);
      if (!currentMessages.length) break;

      const firstMsg = currentMessages[0];

      const rows = await loadMessagesWS(get(conversationId), { firstMsg });
      if (!rows.length) break;

      await tick();

      el = document.getElementById(`msg-${replyToId}`);
    }

    if (el) {
      jumpAndHighlight(el);
    } else {
      console.warn('❌ Không tìm thấy message gốc:', replyToId);
    }
  }

  // function jumpAndHighlight(el) {
  //   el.scrollIntoView({
  //     behavior: 'smooth',
  //     block: 'center'
  //   });

  //   el.classList.add('flash-highlight');
  //   setTimeout(() => {
  //     el.classList.remove('flash-highlight');
  //   }, 1200);
  // }

  async function jumpAndHighlight(el) {
    if (!el) return;

    // 1. Scroll mượt + căn giữa (chuẩn UX chat app)
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest'
    });

    // 2. Chờ scroll + DOM ổn định
    await tick();
    await new Promise((r) => setTimeout(r, 200));

    // 3. Reset animation nếu đã highlight trước đó
    el.classList.remove('flash-highlight');

    // Force reflow để animation chạy lại (rất quan trọng)
    void el.offsetWidth;

    // 4. Add highlight
    el.classList.add('flash-highlight');

    // 5. Auto remove sau khi blink xong
    setTimeout(() => {
      el.classList.remove('flash-highlight');
    }, 2000);
  }

  async function copyMessage(m) {
    try {
      if (!m?.message) return;

      await navigator.clipboard.writeText(m.message);
      console.log('Copied message:', m.message);
    } catch (err) {
      console.error('Copy failed', err);
    } finally {
      closeContextMenu();
    }
  }

  function togglePinnedMessage(m) {
    const targetConversationId = m?.conversationId || get(conversationId);
    if (!targetConversationId || !m?.id) {
      closeContextMenu();
      return;
    }

    if (isPinned) {
      unpinMessageWS(targetConversationId, m.id);
    } else {
      pinMessageWS(targetConversationId, m.id);
    }
    closeContextMenu();
  }

  function uiReactions(arr = []) {
    const map = {};
    for (const r of arr) map[r] = (map[r] || 0) + 1;
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }

  function getCurrentReaction(message) {
    const directReaction =
      message?.myReaction ?? message?.userReaction ?? message?.currentReaction ?? message?.selectedReaction ?? null;

    return typeof directReaction === 'string' && directReaction.trim() ? directReaction : null;
  }

  function handleReactionSelect({ messageId, reaction, remove = false }) {
    if (!remove) {
      optimisticReaction = reaction;
      spawnReactionBurst(reaction);
    } else {
      optimisticReaction = null;
    }

    sendMessage({ type: 'reaction', messageId, reaction, remove });
  }

  $: reactionSummary = uiReactions(msg.reactions || []);
  $: if (msg?.id !== previousMessageId) {
    optimisticReaction = null;
    previousMessageId = msg?.id;
  }
  $: syncedReaction = getCurrentReaction(msg);
  $: currentReaction = syncedReaction ?? optimisticReaction;

  /* ================= CONTEXT MENU ================= */

  function getChatBounds() {
    const boundsEl = msgEl?.closest('.message-scroll') || msgEl?.closest('.message-viewport');
    const rect = boundsEl?.getBoundingClientRect();
    const viewportPadding = CONTEXT_PADDING;
    const viewportBounds = {
      left: viewportPadding,
      right: window.innerWidth - viewportPadding,
      top: viewportPadding,
      bottom: window.innerHeight - viewportPadding
    };

    if (rect) {
      return {
        left: Math.max(viewportBounds.left, rect.left),
        right: Math.min(viewportBounds.right, rect.right),
        top: Math.max(viewportBounds.top, rect.top),
        bottom: Math.min(viewportBounds.bottom, rect.bottom)
      };
    }

    return viewportBounds;
  }

  function clampToBounds(x, y, width, height) {
    const bounds = getChatBounds();
    return {
      x: Math.max(bounds.left + CONTEXT_PADDING, Math.min(x, bounds.right - width - CONTEXT_PADDING)),
      y: Math.max(bounds.top + CONTEXT_PADDING, Math.min(y, bounds.bottom - height - CONTEXT_PADDING))
    };
  }

  async function openContextMenuAt(x, y, mode = 'full') {
    contextMsg = msg;
    contextMenuMode = mode;
    contextMenuReady = false;
    document.getSelection?.()?.removeAllRanges?.();
    menuPos = { x, y };
    bindContextListeners();

    await tick();

    const rect = contextMenuEl?.getBoundingClientRect();
    if (!rect) {
      contextMenuReady = true;
      return;
    }

    menuPos = clampToBounds(x, y, rect.width, rect.height);
    await tick();
    contextMenuReady = true;
  }

  async function openContextMenuNearBubble(mode = 'full') {
    const rect = msgEl?.getBoundingClientRect();
    if (!rect) return;

    const anchorX = fromMe ? rect.right - 24 : rect.left + 8;
    const anchorY = rect.bottom + 8;
    await openContextMenuAt(anchorX, anchorY, mode);
  }

  function handleContextMenu(e) {
    e.preventDefault();
    void openContextMenuAt(e.clientX, e.clientY, 'full');
  }

  function handleTouchStart(e) {
    if (e.touches.length !== 1) return;

    longPressTimer = setTimeout(() => {
      void openContextMenuNearBubble('full');
    }, LONG_PRESS_DELAY);
  }

  function handleTouchEnd() {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }

  function handleTouchMove() {
    // Nếu người dùng kéo → huỷ long press
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }

  function closeContextMenu() {
    contextMsg = null;
    contextMenuMode = 'full';
    contextMenuReady = false;
    unbindContextListeners();
  }

  function startReply(m) {
    replyingTo.set({
      id: m.id,
      senderId: m.from,
      content: m.message,
      type: m.type
    });
    closeContextMenu();
  }

  // Đóng menu khi click ra ngoài / ESC
  function handleGlobalClick(e) {
    if (!(e.target instanceof Element) || !e.target.closest('.context-menu')) {
      closeContextMenu();
    }
  }

  function handleEsc(e) {
    if (e.key === 'Escape') closeContextMenu();
  }

  function handleViewportChange() {
    closeContextMenu();
  }

  function bindContextListeners() {
    if (contextListenersBound) return;

    window.addEventListener('click', handleGlobalClick);
    window.addEventListener('keydown', handleEsc);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    contextListenersBound = true;
  }

  function unbindContextListeners() {
    if (!contextListenersBound) return;

    window.removeEventListener('click', handleGlobalClick);
    window.removeEventListener('keydown', handleEsc);
    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('scroll', handleViewportChange, true);
    contextListenersBound = false;
  }

  // ===== NORMALIZE MEDIA MESSAGE =====
  $: mediaHints = msg.type === 'media' ? getMediaUrlHints(msg.media?.url || msg.message) : { mime: '', name: '' };

  $: mediaUrl =
    msg.type === 'media'
      ? preferInlineMediaUrl(msg.media?.url || msg.message, msg.media?.mime || msg.meta?.mime || mediaHints.mime || '')
      : null;

  $: media =
    msg.type === 'media'
      ? {
          url: mediaUrl,
          name: msg.media?.name || msg.meta?.name || mediaHints.name || mediaUrl?.split('/').pop(),
          mime: msg.media?.mime || msg.meta?.mime || mediaHints.mime || '',
          size:
            msg.media?.size ||
            msg.media?.fileSize ||
            msg.media?.file_size ||
            msg.media?.sizeBytes ||
            msg.media?.size_bytes ||
            msg.meta?.size ||
            msg.meta?.fileSize ||
            msg.meta?.file_size ||
            msg.meta?.sizeBytes ||
            msg.meta?.size_bytes ||
            msg.fileSize ||
            msg.file_size ||
            msg.sizeBytes ||
            msg.size_bytes ||
            msg.size ||
            ''
        }
      : null;
  $: isVisualMedia =
    msg.type === 'album' ||
    (msg.type === 'media' &&
      media?.url &&
      (isImage(media.url, media.mime) || isVideo(media.url, media.mime) || isPDF(media.url, media.mime)));

  $: voicePayload = (() => {
    if (msg.type !== 'audio' || !msg.message) return null;
    try {
      return typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
    } catch {
      return { url: msg.message };
    }
  })();

  function parseLocationPayload(input) {
    if (!input) return null;

    try {
      const parsed = typeof input === 'string' ? JSON.parse(input) : input;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

      const lat = Number(parsed?.lat);
      const lng = Number(parsed?.lng);
      const accuracy = parsed?.accuracy == null ? null : Number(parsed.accuracy);

      if (!Number.isFinite(lat) || lat < -90 || lat > 90) return null;
      if (!Number.isFinite(lng) || lng < -180 || lng > 180) return null;
      if (accuracy !== null && (!Number.isFinite(accuracy) || accuracy < 0)) return null;

      return {
        lat,
        lng,
        accuracy,
        label: typeof parsed.label === 'string' && parsed.label.trim() ? parsed.label.trim() : 'Vị trí hiện tại',
        mapUrl: `https://www.google.com/maps?q=${lat},${lng}`
      };
    } catch {
      return null;
    }
  }

  $: locationPayload = parseLocationPayload(msg.message);
  $: isLocationMessage = msg.type === 'location' || !!locationPayload;

  function parseMaybeJson(value) {
    if (!value || typeof value !== 'string') return value;

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  function normalizeReplyPreview(replyTo) {
    if (!replyTo?.id) return null;

    const rawContent = replyTo.content ?? replyTo.message ?? '';
    const parsed = parseMaybeJson(rawContent);
    const type = replyTo.type || parsed?.sharedType || parsed?.type || 'text';
    const sharedMessage = parsed && typeof parsed === 'object' ? parsed.message || parsed.url || parsed.content || '' : '';
    const message = sharedMessage || rawContent;
    const hints = getMediaUrlHints(message);
    const mime = replyTo.mime || replyTo.media?.mime || parsed?.mime || hints.mime || '';
    const url = preferInlineMediaUrl(replyTo.media?.url || message, mime);
    const name = replyTo.name || replyTo.media?.name || parsed?.name || hints.name || url?.split('/').pop() || '';
    const size =
      replyTo.size ||
      replyTo.fileSize ||
      replyTo.file_size ||
      replyTo.media?.size ||
      replyTo.media?.fileSize ||
      parsed?.size ||
      parsed?.fileSize ||
      parsed?.file_size ||
      '';

    return {
      type,
      message,
      url,
      name,
      mime,
      size,
      isImage: !!url && isImage(url, mime),
      isVideo: !!url && isVideo(url, mime),
      isPDF: !!url && isPDF(url, mime)
    };
  }

  $: replyPreview = normalizeReplyPreview(msg?.reply_to);

  $: album = (() => {
    if (msg.type !== 'album') return null;
    if (!msg.message) return null;

    try {
      const parsed = typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;

      return {
        urls: (parsed.urls || []).map(normalizeUrl),
        fullUrls: (parsed.fullUrls || parsed.urls || []).map(normalizeUrl)
      };
    } catch (err) {
      console.warn('❌ Invalid album message:', msg.message);
      return null;
    }
  })();

  $: sharePayload =
    msg.type === 'share' && msg.message
      ? (() => {
          try {
            return typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
          } catch (e) {
            console.warn('Invalid share payload', msg.message);
            return null;
          }
        })()
      : null;

  function parseCallPayload(message) {
    if (message?.type !== 'call') return null;

    try {
      return typeof message.message === 'string' ? JSON.parse(message.message) : message.message;
    } catch {
      return null;
    }
  }

  $: call = parseCallPayload(msg);

  //$: console.log('msg type call:', call);

  /* ================= MESSAGE STYLE ================= */
  $: styleClass = (() => {
    const width =
      msg.type === 'media' || msg.type === 'album' || msg.type === 'audio' || isLocationMessage
        ? 'max-w-[92%] sm:max-w-[76%] lg:max-w-[68%]'
        : 'max-w-[84%] sm:max-w-[68%]';
    const base = `relative w-fit  min-w-[140px] sm:min-w-[22%]  ${width} text-sm rounded-2xl`;
    const contentLength = typeof msg.message === 'string' ? msg.message.replace(/\s+/g, ' ').trim().length : 0;

    // Sticker: KHÔNG nền – KHÔNG padding – KHÔNG shadow
    if (msg.type === 'sticker') {
      return [base, 'p-0 shadow-none bg-transparent', sameSender ? 'mt-[2px]' : 'mt-2'].join(' ');
    }

    if (isVisualMedia) {
      return [
        base,
        'message-bubble--visual-media p-0 shadow-none bg-transparent border-0 overflow-visible',
        sameSender ? 'mt-[30px] pr-5' : 'mt-5'
      ].join(' ');
    }

    // ✅ Message thường
    const padding =
      contentLength <= 12
        ? 'px-4 py-3 pb-4 shadow-sm'
        : contentLength <= 40
          ? 'px-4.5 py-3.5 pb-4 shadow-sm'
          : contentLength <= 120
            ? 'px-5 py-3.5 pb-4 shadow-sm'
            : 'px-5 py-4 pb-4 shadow-sm';
    const margin = sameSender ? 'mt-[10px]' : 'mt-5';

    const bubble = fromMe
      ? 'bg-gradient-to-b from-[#cfe3ff] via-[#bdd8ff] to-[#acd0ff] text-slate-900 border border-[#9fc2f2]/70 rounded-br-md'
      : 'bg-white/98 text-slate-900 border border-slate-200/80 rounded-bl-md';

    return [base, padding, bubble, margin].join(' ');
  })();
</script>

<!-- ================= MESSAGE WRAPPER ================= -->

{#if msg.status === 1}
  <RecalledMessageBubble {fromMe} {sameSender} time={msg.time || msg.created_at} />
{:else}
  <div class="message-row w-full flex {fromMe ? 'justify-end' : 'justify-start'} group">
    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
      class="message-shell {fromMe ? 'message-shell--me' : 'message-shell--other'}"
      on:mouseenter={() => (messageShellHovered = true)}
      on:mouseleave={() => (messageShellHovered = false)}>
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <div
        bind:this={msgEl}
        id={'msg-' + msg.id}
        on:click|stopPropagation={() => {
          if (suppressClickAfterSelectMany) {
            suppressClickAfterSelectMany = false;
            return;
          }
          if ($selectionMode) {
            toggleSelect(msg.id);
          }
        }}
        on:mousedown={handleMouseDown}
        on:mousemove={handleMouseMove}
        on:mouseup={handleMouseUp}
        on:mouseleave={handleMouseUp}
        on:contextmenu={handleContextMenu}
        class={`${styleClass} ${contextMsg ? 'message-bubble--menu-open' : ''}`}>
        <div class="reaction-fireworks-layer" aria-hidden="true">
          {#each reactionBursts as burst (burst.id)}
            <div
              class="reaction-fireworks"
              class:reaction-fireworks--me={fromMe}
              class:reaction-fireworks--other={!fromMe}>
              {#each burst.particles as particle (particle.id)}
                <span
                  class:reaction-fireworks__spark--icon={!!particle.icon}
                  class="reaction-fireworks__spark"
                  style={`--tx:${particle.tx}px; --ty:${particle.ty}px; --delay:${particle.delay}ms; --size:${particle.size}px; --color:${particle.color};`}>
                  {#if particle.icon}
                    <span class="reaction-fireworks__emoji">{particle.icon}</span>
                  {/if}
                </span>
              {/each}
            </div>
          {/each}
        </div>
        {#if !sameSender}
          <p class="text-[10px] opacity-60 mb-1">
            {fromMe ? 'Bạn' : msg.fromName || 'User ' + msg.from}
          </p>
        {/if}

        <!-- ===== REPLY BLOCK ===== -->
        {#if msg?.reply_to?.id}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <div
            class="mb-1 px-3 py-2
           text-[11px]
           rounded-md
           border-l-4 border-blue-500
           bg-gray-100"
            bind:this={replyEl}
            on:click|stopPropagation={() => scrollToOriginalMessage(msg.reply_to.id)}>
            <!-- DÒNG 1: NGƯỜI ĐƯỢC REPLY -->
            <div class="font-semibold leading-tight text-gray-800">
              {msg.reply_to.sender_id === String(myId) ? 'Bạn' : msg.fromName || 'User ' + msg.from}
            </div>

            <!-- DÒNG 2: NỘI DUNG TIN GỐC -->
            <div class="reply-preview-body">
              {#if replyPreview?.isImage}
                <MediaImagePreview
                  src={replyPreview.url}
                  alt={replyPreview.name || 'Ảnh'}
                  onOpen={() => scrollToOriginalMessage(msg.reply_to.id)} />
              {:else if replyPreview?.url && (replyPreview?.isVideo || replyPreview?.isPDF || replyPreview?.type === 'media')}
                <MediaFilePreview
                  url={replyPreview.url}
                  name={replyPreview.name || 'Tệp đính kèm'}
                  mime={replyPreview.mime}
                  size={replyPreview.size}
                  onOpen={() => scrollToOriginalMessage(msg.reply_to.id)} />
              {:else}
                <MessagePreview preview={replyPreview} />
              {/if}
            </div>
          </div>
        {/if}

        <!-- ===== CONTENT ===== -->
        {#if msg.type === 'text'}
          <TextMessageBubble message={msg.message} />
        {/if}

        {#if msg.type === 'share' && sharePayload}
          <ShareMessageBubble payload={sharePayload} isMine={fromMe} />
        {/if}

        {#if msg.type === 'audio' && voicePayload}
          <VoiceMessage payload={voicePayload} isMine={fromMe} />
        {/if}

        {#if isLocationMessage}
          <LocationMessageBubble
            payload={locationPayload}
            fallback={typeof msg.message === 'string' ? msg.message : 'Không thể hiển thị vị trí'}
            mobile={interactionMode === 'mobile' || $isMobile} />
        {/if}

        {#if msg.type === 'media'}
          {#if isVideo(media.url, media.mime)}
            <MediaVideoPlayer src={media.url} name={media.name} mime={media.mime} />
            <button type="button" class="open-media-viewer" on:click|stopPropagation={() => openMediaPreview()}>
              Xem trong danh sách tệp
            </button>
          {:else if isPDF(media.url, media.mime)}
            <PdfPreview
              url={media.url}
              name={media.name}
              size={media.size}
              mime={media.mime}
              onOpen={() => openMediaPreview()} />
          {:else if isImage(media.url, media.mime)}
            <MediaImagePreview src={media.url} alt={media.name || 'Ảnh'} onOpen={() => openMediaPreview()} />
          {:else}
            <MediaFilePreview
              url={media.url}
              name={media.name}
              mime={media.mime}
              size={media.size}
              onOpen={() => openMediaPreview()} />
          {/if}
        {/if}

        {#if msg.type === 'album' && album}
          <MediaAlbumPreview
            urls={album.urls}
            fullUrls={album.fullUrls}
            onOpen={({ index }) => openMediaPreview(index)} />
        {/if}

        {#if msg.type === 'call' && call}
          <CallMessageBubble {call} isMine={fromMe} />
        {/if}

        {#if msg.type === 'sticker'}
          <Sticker src={normalizeUrl(msg.message)} />
        {/if}

        {#if msg.type === 'link'}
          <LinkMessageBubble href={msg.message} />
        {/if}

        <!-- ===== TIME ===== -->
        <p class="text-[9px] text-right opacity-60 mt-1">
          {formatTimeLabel(msg.time || msg.created_at)}
        </p>

        {#if msg.type !== 'sticker'}
          <BubbleActions
            messageId={msg.id}
            {fromMe}
            {interactionMode}
            {currentReaction}
            {reactionSummary}
            active={messageShellHovered || useMobileActions}
            selectionMode={$selectionMode}
            alwaysShowReaction={reactionSummary.length}
            alwaysShowReactionPopup={messageShellHovered && !useMobileActions}
            onReply={() =>
              startReply({
                id: msg.id,
                from: msg.from,
                message: msg.message,
                type: msg.type
              })}
            onShare={() => quickShare(msg)}
            onDetails={openDetailsMenu}
            onSelectReaction={handleReactionSelect} />
        {/if}

        {#if $selectionMode}
          <MessageSelectionOverlay selected={isSelected} />
        {/if}
      </div>
    </div>
  </div>
{/if}

<!-- ================= CONTEXT MENU ================= -->
<ContextMenu
  bind:menuRef={contextMenuEl}
  visible={!!contextMsg}
  ready={contextMenuReady}
  x={menuPos.x}
  y={menuPos.y}
  mode={contextMenuMode}
  isMine={String(contextMsg?.from) === String(myId)}
  {isPinned}
  selectionMode={$selectionMode}
  selectedCount={$selectedMessageIds.size}
  onShare={() => shareMessage(contextMsg)}
  onReply={() => startReply(contextMsg)}
  onTogglePin={() => togglePinnedMessage(contextMsg)}
  onCopy={() => copyMessage(contextMsg)}
  onSelectMany={() => selectManyMessage(contextMsg)}
  onRecall={() => {
    sendMessage({
      type: 'recall',
      messageId: contextMsg.id
    });
    closeContextMenu();
  }}
  onDelete={() => {
    sendMessage({
      type: 'delete',
      messageId: contextMsg.id
    });
    closeContextMenu();
  }}
  onClearSelection={clearSelection} />

{#if showGallery}
  <PreviewMedia
    messages={mediaMessages.length ? mediaMessages : [msg]}
    initialMessageId={msg.id}
    initialAlbumIndex={previewAlbumIndex}
    onClose={() => (showGallery = false)} />
{/if}

<style>
  .open-media-viewer {
    margin-top: 0.25rem;
    padding: 0.3rem 0.5rem;
    border-radius: 0.4rem;
    color: #075985;
    background: #e0f2fe;
    font-size: 0.72rem;
  }
  .message-row {
    align-items: flex-end;
    gap: 0.65rem;
    overflow-x: clip;
    user-select: text;
  }

  :global(.message-bubble--menu-open),
  :global(.message-bubble--menu-open *) {
    -webkit-touch-callout: none !important;
    -webkit-user-select: none !important;
    user-select: none !important;
  }

  .message-shell {
    position: relative;
    display: flex;
    align-items: flex-end;
    width: 100%;
    flex: 1 1 auto;
    max-width: 100%;
    overflow: visible;
    min-width: 0;
  }

  .message-shell--me {
    justify-content: flex-end;
  }

  .message-shell--other {
    justify-content: flex-start;
  }

  .message-bubble--visual-media :global(.image-preview) {
    width: min(19rem, 72vw);
    margin-top: 0;
  }

  .message-bubble--visual-media :global(.image-preview img) {
    width: 100%;
  }

  .message-bubble--visual-media :global(.video-shell) {
    margin-top: 0;
  }

  .reply-preview-body {
    margin-top: 0.35rem;
    min-width: 0;
  }

  .reply-preview-body :global(.image-preview) {
    width: min(10.5rem, 52vw);
    max-width: 100%;
    margin-top: 0.15rem;
    border-radius: 0.5rem;
  }

  .reply-preview-body :global(.image-preview img) {
    width: 100%;
    max-height: 7rem;
    object-fit: cover;
  }

  .reply-preview-body :global(.file-preview) {
    width: min(100%, 14rem);
    margin-top: 0.15rem;
    padding: 0.42rem 0.5rem;
  }

  @media (max-width: 640px) {
    .message-bubble--visual-media :global(.image-preview) {
      width: min(18rem, 76vw);
    }
  }

  @media (max-width: 768px) {
    .message-row {
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
    }

    .message-row :global(*) {
      -webkit-touch-callout: none !important;
      -webkit-user-select: none !important;
      user-select: none !important;
    }
  }

  .reaction-fireworks-layer {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: inherit;
    pointer-events: none;
    z-index: 24;
  }

  .reaction-fireworks {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 0;
    height: 0;
    transform: translate(-50%, -50%);
    filter: drop-shadow(0 0 14px rgba(255, 255, 255, 0.42));
  }

  .reaction-fireworks__spark {
    position: absolute;
    left: 0;
    top: 0;
    display: grid;
    place-items: center;
    width: var(--size);
    height: var(--size);
    border-radius: 999px;
    background: color-mix(in srgb, var(--color) 22%, transparent);
    box-shadow:
      0 0 0 1px color-mix(in srgb, var(--color) 44%, white),
      0 0 14px color-mix(in srgb, var(--color) 55%, white);
    transform: translate(-50%, -50%) scale(0.35);
    opacity: 0;
    animation: reaction-spark-burst 1180ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
    animation-delay: var(--delay);
  }

  .reaction-fireworks__spark--icon {
    background: transparent;
    box-shadow: none;
    text-shadow: 0 0 12px rgba(255, 255, 255, 0.85);
  }

  .reaction-fireworks__emoji {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: calc(var(--size) * 0.92);
    line-height: 1;
    transform: translateY(-1px);
    filter: saturate(1.1);
  }

  @keyframes reaction-spark-burst {
    0% {
      transform: translate(-50%, -50%) scale(0.35);
      opacity: 0;
      filter: blur(0.15px);
    }
    12% {
      opacity: 1;
    }
    62% {
      opacity: 1;
    }
    100% {
      transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(0.08);
      opacity: 0;
      filter: blur(1.15px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .reaction-fireworks__spark {
      animation: none;
    }
  }

  /* .message-actions-slot {
    position: absolute;
    top: 50%;
    flex: 0 0 auto;
    display: flex;
    align-items: flex-end;
    transform: translateY(-50%);
    z-index: 18;
    pointer-events: none;
    max-width: min(11rem, 38vw);
  }

  .message-actions-slot--before {
    right: 0.25rem;
  }

  .message-actions-slot--after {
    left: auto;
    right: 0.25rem;
  } */

  @keyframes flash-bg {
    0% {
      background-color: rgba(59, 130, 246, 0.25);
    }
    100% {
      background-color: transparent;
    }
  }

  .flash-highlight {
    position: relative;
    animation: flash-border 1.2s ease-in-out 3;
    border-radius: 16px;
  }

  .flash-highlight::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 20px;
    border: 2px solid rgba(59, 130, 246, 0.9);
    animation: pulse-border 1.2s ease-in-out 3;
    pointer-events: none;
  }

  @keyframes flash-border {
    0% {
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.25);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
    }
  }

  @keyframes pulse-border {
    0% {
      opacity: 0;
      transform: scale(0.96);
    }
    50% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0;
      transform: scale(1.02);
    }
  }
</style>
